import { BoardID } from 'BoardID';
import {
  BoardDetails,
  BoardHash,
  BoardParams,
  BoardType,
  Dimensions2D,
  Material,
  ParamsPanelSelection,
  Renderable,
  RenderableMetadata,
  Settings,
  Units,
} from 'Types';
import { v4 as uuid } from 'uuid';

export default abstract class BoardBase<T extends BoardType = BoardType> {
  public static isDevOnly: boolean = false;

  public units: Units;

  constructor(
    public type: T,
    public id: BoardID,
    public name: string,
    public params: BoardParams<T>,
    units?: Units
  ) {
    this.units = units ?? Units.IN;
  }

  public getDimensions(): Dimensions2D {
    let maxX = 0;
    let maxY = 0;

    for (const { rect } of this.getRenderables()) {
      maxX = Math.max(maxX, rect.left + rect.width);
      maxY = Math.max(maxY, rect.top + rect.height);
    }

    return {
      height: maxY,
      width: maxX,
    };
  }

  abstract getRenderables(): Array<Renderable<T>>;

  abstract getIsRenderableHighlighted(
    renderable: Renderable<T>,
    selection: Renderable<T>
  ): boolean;

  abstract getBoardDetails(settings: Settings): BoardDetails;

  abstract handleSelectedSizeChange(
    selectedRenderable: Renderable<T>,
    size: number,
    dimension: 'height' | 'width'
  ): BoardBase<T>;

  abstract handleSelectedMaterialChange(
    selectedRenderable: Renderable<T>,
    material: Material
  ): BoardBase<T>;

  abstract previewSelectionToParamsPanelSelection(
    selection: RenderableMetadata<T>
  ): null | ParamsPanelSelection<T>;

  abstract paramsPanelSelectionToPreviewSelection(
    selection: ParamsPanelSelection<T>
  ): null | RenderableMetadata<T>;

  // handleDragEnd?: (
  //   board: Boardish<T>,
  //   selectedRenderable: Renderable<T>,
  //   delta: XYCoord,
  // ) => Board<T>;

  abstract scaleParams(params: BoardParams<T>, scale: number): BoardParams<T>;

  getHash(): BoardHash {
    return btoa(
      JSON.stringify({
        type: this.type,
        id: this.id,
        name: this.name,
        params: this.params,
        units: this.units,
      })
    );
  }

  isEqual(board: BoardBase): boolean {
    return (
      this.type === board.type &&
      this.id === board.id &&
      this.name === board.name &&
      this.units === board.units &&
      JSON.stringify(this.params) === JSON.stringify(board.params)
    );
  }

  duplicate(): BoardBase<T> {
    // @ts-ignore
    return new this.constructor(
      uuid() as BoardID,
      this.name,
      this.params,
      this.units
    );
  }

  rename(name: string): BoardBase<T> {
    // @ts-ignore
    return new this.constructor(this.id, name, this.params, this.units);
  }

  updateUnits(units: Units): BoardBase<T> {
    // @ts-ignore
    return new this.constructor(this.id, this.name, this.params, units);
  }

  updateParams(paramUpdates: Partial<BoardParams<T>>): BoardBase<T> {
    // @ts-ignore
    return new this.constructor(
      this.id,
      this.name,
      { ...this.params, ...paramUpdates },
      this.units
    );
  }
}
